import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Modal, Form, Button, Row, Col, Input, message, Table } from 'antd';
import styled from 'styled-components';
import moment from 'utils/moment';
import PhoneInput from 'components/PhoneInput';
import BirthdayInput from 'components/BirthdayInput';
import apis from 'services/apis';
import { ICustomerDetailRes, ICustomerTransactionRes } from 'types/customer';
import { useAppDispatch } from 'store/hooks';
import aptActions from 'services/actions/apt';
import aptSelectors from 'services/selectors/apt';
import Text from 'components/Text';
import { formatCurrency } from 'utils/formatCurrency';
import CustomerSurveyModal, { CustomerSurveyModalRef } from './CustomerApptConfirm/CustomerSurveyModal';
import { loadingPageSignal } from 'components/LoadingPage';
import { formatPhone } from 'utils/formatPhone';
import { CUSTOMER_DETAIL_MODAL_INDEX } from 'constants/variables';

export interface ICustomerForm {
  id?: number,
  firstName?: string
  lastName?: string
  phone?: string
  dob?: string
  surveyInfo?: string[],
  point?: number,
  note?: string,
}

export type CustomerDetailModalRef = {
  open: (customerId?: number) => Promise<ICustomerForm | undefined>;
  close: () => void;
};

const CustomerDetailModal = forwardRef<CustomerDetailModalRef>((props, ref) => {
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<ICustomerForm>();
  const callbackResolve = useRef<(data: ICustomerForm | undefined) => void>(() => undefined);
  const transactions = aptSelectors.getCustomerTransactions();
  const surveyInfos = aptSelectors.getShopSurveyInfo();
  const customerSurveyModalRef = useRef<CustomerSurveyModalRef>(null);
  const dispatch = useAppDispatch();

  const fetchCustomer = async (id: number) => {
    try {
      const res = await apis.getCustomerDetail(id);
      if (res?.data?.payload) {
        return res.data.payload;
      }
    } catch (error) {
      message.error('Customer not found');
    }
  };

  const handleOpen: CustomerDetailModalRef['open'] = async (customerId?: number) => {
    form.resetFields();
    loadingPageSignal.value = true;
    try {
      if (customerId) {
        dispatch(aptActions.getCustomerTransactions.fetch(customerId));
        dispatch(aptActions.getShopSurveyInfo.fetch());
        const customer: ICustomerDetailRes | null = await fetchCustomer(customerId);

        if (!customer) {
          return;
        }
        form.setFieldsValue({
          id: customer.customerId,
          dob: customer.dob ? moment(customer.dob, 'MM/DD').format('MM-DD') : '',
          firstName: customer.customerName.split(/\s(.*)/)[0],
          lastName: customer.customerName.split(/\s(.*)/)[1],
          phone: customer.customerPhone ? formatPhone(customer.customerPhone) : '',
          surveyInfo: customer.surveyOption?.split(', ') ?? [],
          note: customer.note,
        });
      }

      setOpen(true);
    } catch (error) {

    } finally {
      loadingPageSignal.value = false;
    }

    return new Promise((resolve) => {
      callbackResolve.current = resolve;
    });
  };

  const handleClose = () => {
    callbackResolve.current(undefined);
    setOpen(false);
  };

  const handleSubmit = async (values: ICustomerForm) => {
    try {
      await form.validateFields();
      callbackResolve.current(values);
      setOpen(false);
    } catch (err) {
      console.log('Validation failed');
    }
  };

  const handleSurvey = async () => {
    const surveyOptions = (surveyInfos ?? []).filter(i => (form.getFieldValue('surveyInfo')?.includes(i.option)));

    const surveys = await customerSurveyModalRef.current?.open({
      options: surveyOptions,
      others: (form.getFieldValue('surveyInfo')?.join(', ') ?? '').replace(surveyOptions.map(i => i.option).join(', '), '')
    });
    if (!surveys) return;

    form.setFieldValue('surveyInfo', [...surveys.options?.map(i => i.option)?.filter(Boolean) || [], surveys.others]);
  };

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  const columns = [
    {
      title: 'TICKET', dataIndex: 'ticketNumber', render: (values: number) =>
        <Text variant='H8'>
          {`#${values}`}
        </Text>
    },
    { title: 'DATE & TIME', dataIndex: 'createdDate' },
    { title: 'SERVICES', dataIndex: 'serviceNames', render: (values: string[]) => values?.join(', ') },
    { title: 'STAFFS', dataIndex: 'staffNames', render: (values: string[]) => values?.join(', ') },
    {
      title: 'AMOUNT',
      render: (o: ICustomerTransactionRes) =>
        formatCurrency(o.payments?.reduce((prev: number, i: any) => prev + i.amount, 0)),
    },
    {
      title: 'PAYMENT TYPE',
      render: (o: ICustomerTransactionRes) =>
        o.payments?.map(i => i.paymentMethod).join(', '),
    },
    {
      title: 'CONSENT FORM', dataIndex: 'urlConsentForm',
      render: (value: string) => (
        <TextLink href={value} target="_blank" rel="noreferrer">
          Consent.pdf
        </TextLink>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      width='80vw'
      title="Update Customer"
      closeIcon={null}
      zIndex={CUSTOMER_DETAIL_MODAL_INDEX}
    >
      <Header>
        <span>Update Customer</span>
        <button style={{ right: 16, position: 'absolute', bottom: 0, top: 0 }} onClick={handleClose}><p className="text-action">CLOSE</p></button>
      </Header>
      <Container>
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label="First Name"
                rules={[{ required: true }]}
              >
                <Input placeholder='Enter First Name' />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="lastName"
                label="Last Name"
              >
                <Input placeholder='Enter Last Name' />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Phone Number"
                rules={[{ required: true }]}
              >
                <PhoneInput />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="dob" label="Birthday">
                <BirthdayInput />
              </Form.Item>
            </Col>
          </Row>

          <button type='button' onClick={handleSurvey} style={{ flex: 1 }}>
            <Form.Item name="surveyInfo" label="Any Problem the nails technician should know?">
              <Input readOnly={true} className='survey' placeholder="Select survey..." />
            </Form.Item>
          </button>

          <Form.Item name="note" label="Notes">
            <Input.TextArea rows={3} />
          </Form.Item>

          {!!transactions?.length && <>
            <SectionTitle>Transaction History</SectionTitle>
            <TableStyled
              columns={columns}
              dataSource={transactions ?? []}
              rowKey="ticketNumber"
              loading={!transactions}
              scroll={{ y: 240 }}

            />
          </>}
        </Form>
        <CustomerSurveyModal ref={customerSurveyModalRef} />
      </Container>
      <Footer>
        <button className='btn btn-delete' onClick={handleClose}>
          <Text className="label">ClOSE</Text>
        </button>
        <button className='btn btn-update' onClick={() => form.submit()}>
          <Text className="label">{'UPDATE'}</Text>
        </button>
      </Footer>

    </Modal>
  );
});

export default CustomerDetailModal;

const Container = styled.div`
  padding: 16px;
  position: relative;

  input {
    border-radius: 6px;
    border: 1px solid #86909C;
    background: #FFF;
    display: flex;
    width: 100%;
    height: 44px;
    align-items: center;
    padding: 0px 11px;

    font-family: Poppins;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    line-height: 1.5;
    outline: none;
    &:focus, &:hover {
      outline: none;
      border-color: #fc7c51;
    }
    &::placeholder {
      color:#dadada;
    }
  }

  .survey {
    cursor: pointer;
  }
`;

const Header = styled.div`
  display: flex;
  height: 48px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  border-bottom: 1px solid #148CF1;
  background: #E8F3FF;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.15);
  position: relative;
  span {
    align-self: stretch;
    color: #1D2129;
    text-align: center;
    font-family: Poppins;
    font-size: 24px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    text-transform: uppercase;
  }
  .text-action {
    color: #F05326;
    font-family: Poppins;
    font-size: 22px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px; /* 133.333% */
    white-space: nowrap;
  }
`;

const Footer = styled(Row)`
position: sticky;
padding: 0.75rem 1.5rem;
margin-top: 1rem;
gap: 1rem;
bottom: 0;
right: 0;
left: 0;
background-color: #fff;
.btn {
  display: flex;
  height: 48px;
  padding: 10px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;

  .label {
    text-align: center;

    /* Body 1 */
    font-family: Poppins;
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px; /* 150% */
  }
  
}
  .btn-delete, .btn-close {
    border-radius: 2px;
    border: 1px solid #7B7B7B;
    background: #7B7B7B;
    .label {
      color: #fff;
    }
  }

  .btn-update {
    border-radius: 2px;
    background: #232F3E;
    .label {
      color: #FFF;
    }

    &:disabled {
      opacity: 0.7;
    }
  }
  .btn-delete {
    background: #fff;
    .label {
      color: #7B7B7B;
    }
  }
  .btn-confirm {
    border-radius: 2px;
    background: #0085E7;
    .label {
      color: #FFF;
    }

    &:disabled {
      opacity: 0.7;
    }
  }
`;

const SectionTitle = styled.div`
  font-weight: bold;
  margin-top: 16px;
`;

const TextLink = styled.a`
  color: #1a1eff;
  cursor: pointer;
`;

const TableStyled = styled(Table)`
align-self: stretch;
width: 100%;
.dollar-input-selector {
  justify-content: flex-end;
  height: auto;
}
&.ant-table-wrapper .ant-table-body {
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
}
&.ant-table-wrapper .ant-pagination.ant-table-pagination {
  margin: 8px 0;
}
&.ant-table-wrapper .ant-table.ant-table-empty .ant-table-thead>tr>th {
  position: relative;
}
&.ant-table-wrapper .ant-table-thead>tr>th, .ant-table-wrapper .ant-table-thead>tr>th{
  border-bottom: 1px solid #CCD4DC;
  border-right: 1px solid #FFF;
  background: #484F58;
  color: #FFF;
  font-family: Poppins;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  padding: 4px 8px;
}
&.ant-table-wrapper .ant-table-tbody>tr>td.ant-table-cell {
  padding: 4px 16px;
  .ant-select-selection-item, .ant-input {
    font-size: 18px;
  }

  .ant-input.ant-input-disabled  {
    color: #1D2129;
  }
  &.number-row {
    text-align: right !important;
  }
}

&.ant-table-wrapper .ant-table-tbody>.ant-table-row.colorSet-default td{
  background: #FFFFFF;
}
&.ant-table-wrapper .ant-table-tbody>.ant-table-row.colorSet-0 td{
  background: #E5E3E6;
}
&.ant-table-wrapper .ant-table-tbody>.ant-table-row.colorSet-1 td{
  background: #c7d7f6;
}
&.ant-table-wrapper .ant-table-tbody>.ant-table-row.colorSet-2 td{
  background: #E4D3E7;
}
&.ant-table-wrapper .ant-table-tbody>.ant-table-row.colorSet-3 td{
  background: #FDFEDB;
}
&.ant-table-wrapper .ant-table-tbody>.ant-table-row.colorSet-4 td{
  background: #E6FAE2;
}
&.ant-table-wrapper .ant-table-tbody>.ant-table-row.colorSet-5 td{
  background: #F2CEE6;
}
&.ant-table-wrapper .ant-table-tbody>.ant-table-row.colorSet-6 td{
  background: #FFFFFF;
}

&.ant-table-wrapper .ant-table-summary .ant-table-cell {
  color: #1D2129;
  font-family: Poppins;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  &.value {
    text-align: right;
  }
  &.action, &.total {
    z-index: 10;
    &::after {
      box-shadow: none;
    }
  }
  &.action {
    right: 0;
  }
}
.ant-pagination.ant-table-pagination {
  align-items: center;
}
`;
